'use client'

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import { SearchProvider } from '@/contexts/SearchContext'
import { ChatProvider, useChat } from '@/contexts/ChatContext'
import { WorkspaceProvider } from '@/contexts/WorkspaceContext'
import { PageStateProvider } from '@/lib/page-state/PageStateProvider'
import { AppSidebar } from '@/components/AppSidebar'
import { LobbyHeader } from '@/components/LobbyHeader'
import { LobbyHeaderMetaProvider } from '@/contexts/LobbyHeaderMetaContext'
import { CampaignChatPanel } from '@/components/CampaignChatPanel'
import { DataRefreshListener } from '@/components/DataRefreshListener'
import { ShellPanelsProvider } from '@/contexts/ShellPanelsContext'
import { GlobalDropOverlay } from '@/components/GlobalDropOverlay'
import { MobileHeader } from '@/components/mobile/MobileHeader'
import { MobileWorkspaceDrawer } from '@/components/mobile/MobileWorkspaceDrawer'
import { MobileAtriumChatSheet } from '@/components/mobile/MobileAtriumChatSheet'
import { MobileHeaderProvider } from '@/contexts/MobileHeaderContext'
import { WorkspaceSettingsPanel } from '@/components/WorkspaceSettingsPanel'
import { WorkspaceForm } from '@/components/workspace-form'
import { SlideOutPanel } from '@/components/ui/slide-out-panel'
import { CommandSearchDialog } from '@/components/command-search/CommandSearchDialog'
import { AccountContent } from '@/components/AccountContent'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { START_PAGE_AUTO_SEND_KEY, START_PAGE_FLOW_INTENT_KEY, START_PAGE_PENDING_MESSAGE_KEY } from '@/lib/start-chat-pending'
import { useCachedUser } from '@/hooks/useCachedUser'
import { createClient } from '@/utils/supabase/client'
import { canShellArrowKeyToggleSidebars } from '@/lib/shell-arrow-key-toggle'
import { cn } from '@/lib/utils'
import { LoggedOutBanner } from '@/components/LoggedOutBanner'
import type { SidebarRailMode } from '@/components/AppSidebarUserMenu'
import type { Tables } from '@/utils/schema'

// A/B: layout/sidebar transitions set to 0 for an instant feel.
// Original values preserved in git history if we want to revert.
const APP_SIDEBAR_OVERLAY_TRANSITION_MS = 200
/** If transitionend doesn't run (motion-reduce, etc.). */
const APP_SIDEBAR_OVERLAY_UNMOUNT_FALLBACK_MS = 0

/**
 * Chat-fullscreen nav drawer: transforms + transitions (not tailwind animate-out) —
 * animate-out resets to resting styles briefly after the animation, which caused a flash.
 */
function AppSidebarLobbyOverlay({
  open,
  onOpenChange,
  children,
}: {
  open: boolean
  onOpenChange: (next: boolean) => void
  children: ReactNode
}) {
  const [shellMounted, setShellMounted] = useState(open)
  const [paintOpen, setPaintOpen] = useState(false)
  const prevHadOpenProp = useRef(false)

  useLayoutEffect(() => {
    if (open) setShellMounted(true)

    if (!open) {
      setPaintOpen(false)
      prevHadOpenProp.current = false
      return undefined
    }

    const closedToOpen = !prevHadOpenProp.current
    prevHadOpenProp.current = true

    if (!closedToOpen) return undefined

    setPaintOpen(false)
    let innerRaf = 0
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => setPaintOpen(true))
    })
    return () => {
      cancelAnimationFrame(outerRaf)
      cancelAnimationFrame(innerRaf)
    }
  }, [open])

  useEffect(() => {
    if (open || !shellMounted) return
    const id = window.setTimeout(() => setShellMounted(false), APP_SIDEBAR_OVERLAY_UNMOUNT_FALLBACK_MS)
    return () => window.clearTimeout(id)
  }, [open, shellMounted])

  const reveal = paintOpen && open

  const onPanelTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (open || e.target !== e.currentTarget) return
    if (e.propertyName !== 'transform') return
    // Defer unmount past compositing; avoids a one-frame flicker of resting styles after close.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (open) return
        setShellMounted(false)
      })
    })
  }

  if (!shellMounted) return null

  const transitionCls = 'duration-200 ease-out motion-reduce:transition-none motion-reduce:duration-0'

  return (
    <div className="fixed inset-0 z-[120] overflow-hidden">
      <div
        role="presentation"
        className={cn(
          'absolute inset-0 cursor-default bg-black/40 transition-opacity',
          transitionCls,
          reveal ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        style={{ transitionDuration: `${APP_SIDEBAR_OVERLAY_TRANSITION_MS}ms` }}
        onClick={() => onOpenChange(false)}
      />
      <div
        onTransitionEnd={onPanelTransitionEnd}
        className={cn(
          'fixed left-0 top-0 z-[11] flex h-full w-72 shrink-0 flex-col overflow-hidden bg-atrium-sidebar shadow-2xl opacity-100',
          'transition-transform ease-out motion-reduce:transition-none',
          reveal ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{ transitionDuration: `${APP_SIDEBAR_OVERLAY_TRANSITION_MS}ms` }}
      >
        {children}
      </div>
    </div>
  )
}

function LobbyInner({
  children,
  isLoggedIn,
  showLoggedOutBanner,
  initialSidebarRailMode,
}: {
  children: React.ReactNode
  isLoggedIn: boolean
  showLoggedOutBanner: boolean
  initialSidebarRailMode: SidebarRailMode | null
}) {
  const pathname = usePathname() ?? ''
  const {
    openChat,
    setCurrentThreadId,
    closeChat,
    collapseAppSidebar,
    toggleChat,
    toggleAppSidebar,
    appSidebarOverlayOpen,
    setAppSidebarOverlayOpen,
    completingOnboarding,
    godMode,
    setGodMode,
  } = useChat()

  // Stephen 2026-05-13: entering /admin or /work auto-switches the chat to
  // god mode. These routes ARE the team-brain admin surface, so the
  // conversation context should match. We only flip on entry — leaving the
  // route doesn't auto-revert because the user might still be steering the
  // brain from a different page. The chat header toggle remains the manual
  // override either way.
  const isGodModeRoute = pathname.startsWith('/work') || pathname.startsWith('/admin')
  useEffect(() => {
    if (isGodModeRoute && !godMode) {
      setGodMode(true)
    }
  }, [isGodModeRoute, godMode, setGodMode])
  const hideAppSidebar = pathname === '/chat'
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem(START_PAGE_PENDING_MESSAGE_KEY)
      if (pending?.trim()) {
        sessionStorage.removeItem(START_PAGE_PENDING_MESSAGE_KEY)
        const autoSend = sessionStorage.getItem(START_PAGE_AUTO_SEND_KEY) === '1'
        sessionStorage.removeItem(START_PAGE_AUTO_SEND_KEY)
        const intent = (sessionStorage.getItem(START_PAGE_FLOW_INTENT_KEY) || 'campaign') as 'campaign' | 'video' | 'brand'
        sessionStorage.removeItem(START_PAGE_FLOW_INTENT_KEY)
        openChat(intent, { seedMessage: pending.trim(), autoSendSeed: autoSend })
      }
    } catch {
      // ignore
    }
  }, [isLoggedIn, openChat, pathname])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      if (e.repeat) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (!canShellArrowKeyToggleSidebars()) return
      if (e.key === 'ArrowLeft' && !hideAppSidebar) {
        e.preventDefault()
        toggleAppSidebar()
        return
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        toggleChat()
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [hideAppSidebar, toggleAppSidebar, toggleChat])

  useEffect(() => {
    const yieldEscapeToOpenModal = (): boolean => {
      if (
        document.querySelector(
          '[data-slot="dialog-content"][data-state="open"], [data-slot="alert-dialog-content"][data-state="open"], [data-slot="sheet-content"][data-state="open"]',
        )
      ) {
        return true
      }
      const ae = document.activeElement
      if (!(ae instanceof Element)) return false
      if (ae.closest('[cmdk-root]')) return true
      if (ae.closest('[data-radix-popover-content][data-state="open"], [data-radix-dropdown-menu-content][data-state="open"], [data-radix-select-content][data-state="open"], [data-radix-context-menu-content][data-state="open"]')) {
        return true
      }
      return false
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (e.repeat) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.defaultPrevented) return
      if (yieldEscapeToOpenModal()) return
      // Dedicated /chat UI handles its own Escape; avoid preventDefault with no-op here.
      if (hideAppSidebar) return

      // Overlay sidebar close takes priority (mobile hamburger overlay).
      if (appSidebarOverlayOpen) {
        e.preventDefault()
        setAppSidebarOverlayOpen(false)
        return
      }

      e.preventDefault()
      closeChat()
      if (!hideAppSidebar) collapseAppSidebar()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    appSidebarOverlayOpen,
    collapseAppSidebar,
    closeChat,
    hideAppSidebar,
    setAppSidebarOverlayOpen,
  ])

  // Mobile (<lg): replace sidebar with header+footer shell; desktop unchanged.
  // The mobile chrome (header/footer) is `lg:hidden`; desktop chrome (AppSidebar,
  // CampaignChatPanel) is wrapped to `hidden lg:contents` so it never paints on mobile.
  // Phase 2 adds the workspace bottom drawer with drill-nav.
  const [mobileAtriumChatOpen, setMobileAtriumChatOpen] = useState(false)
  const [mobileAtriumChatEverOpened, setMobileAtriumChatEverOpened] = useState(false)

  const [mobileWorkspaceDrawerOpen, setMobileWorkspaceDrawerOpen] = useState(false)
  const [mobileWorkspaceSettingsOpen, setMobileWorkspaceSettingsOpen] = useState(false)
  const [mobileWorkspaceSettingsSection, setMobileWorkspaceSettingsSection] = useState<string>('general')
  const [mobileWorkspaceSettingsEverOpened, setMobileWorkspaceSettingsEverOpened] = useState(false)
  const [mobileCreateWorkspaceOpen, setMobileCreateWorkspaceOpen] = useState(false)
  const [mobileCreateWorkspaceEverOpened, setMobileCreateWorkspaceEverOpened] = useState(false)
  const [commandSearchOpen, setCommandSearchOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [avatarRefreshKey, setAvatarRefreshKey] = useState(0)

  const handleOpenAtriumChat = useCallback(() => {
    setMobileAtriumChatEverOpened(true)
    setMobileAtriumChatOpen(true)
  }, [])

  const handleSearchOpenChat = useCallback(
    (intent: string) => {
      openChat(intent as 'campaign' | 'video' | 'brand' | null)
    },
    [openChat],
  )

  const handleSearchSelectThread = useCallback(
    (threadId: string) => {
      setCurrentThreadId(threadId)
      openChat(null)
    },
    [openChat, setCurrentThreadId],
  )

  return (
    <MobileHeaderProvider>
      <div className="relative flex h-screen w-full flex-col overflow-hidden bg-atrium-background">
        {showLoggedOutBanner ? <LoggedOutBanner /> : null}
        <div className="relative flex min-h-0 flex-1 overflow-hidden">
        {/* Desktop sidebar — hidden <lg */}
        <div className="hidden lg:contents">
          {hideAppSidebar ? null : (
            <AppSidebar initialRailMode={initialSidebarRailMode} />
          )}
        </div>
        {/* App column + desktop chat — replaces MobileSwipePanes */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {/* Mobile header — only painted <lg */}
            <div className="lg:hidden">
              <MobileHeader
                onOpenSearch={() => setCommandSearchOpen(true)}
                onOpenAccount={() => setAccountOpen(true)}
                onOpenChat={handleOpenAtriumChat}
                accountAvatar={<MobileAccountAvatar refreshKey={avatarRefreshKey} />}
                onOpenSidebar={() => setAppSidebarOverlayOpen(true)}
              />
            </div>
            <LobbyHeaderMetaProvider>
              <div className="contents">
                <LobbyHeader
                  onOpenCommandSearch={() => setCommandSearchOpen(true)}
                  onAccountOpenChange={setAccountOpen}
                  avatarRefreshKey={avatarRefreshKey}
                  hideAppSidebar={hideAppSidebar}
                />
                <main className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-atrium-background">
                  <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
                </main>
              </div>
            </LobbyHeaderMetaProvider>
            {/* Mobile chat opens from the header wordmark/chat affordance. */}
          </div>
          {/* Desktop chat panel */}
          <div className="hidden lg:contents">
            <CampaignChatPanel />
          </div>
        </div>
        </div>
      </div>
      {/* Overlay sidebar: always available so mobile hamburger can open it.
          On desktop non-fullscreen, appSidebarOverlayOpen stays false (no trigger),
          so shellMounted never sets and nothing renders. */}
      <AppSidebarLobbyOverlay open={appSidebarOverlayOpen} onOpenChange={setAppSidebarOverlayOpen}>
        <AppSidebar mode="overlay" />
      </AppSidebarLobbyOverlay>
      {/* Full-screen Atrium chat — lazy-mount after first open */}
      {mobileAtriumChatEverOpened ? (
        <MobileAtriumChatSheet
          open={mobileAtriumChatOpen}
          onOpenChange={setMobileAtriumChatOpen}
          isLoggedIn={isLoggedIn}
        />
      ) : null}
      <CommandSearchDialog
        open={commandSearchOpen}
        onOpenChange={setCommandSearchOpen}
        onOpenChat={handleSearchOpenChat}
        onSelectThread={handleSearchSelectThread}
      />
      <AccountContent
        open={accountOpen}
        onOpenChange={setAccountOpen}
        onAvatarUpdated={() => setAvatarRefreshKey((prev) => prev + 1)}
      />
      {/*
       * Mobile workspace drawer + the settings/create panels it triggers.
       * These mount unconditionally because:
       *  - their visibility is gated by their own `open` prop, and
       *  - on desktop the trigger (mobile footer) is `lg:hidden`, so they
       *    never get opened from a desktop session, so they never paint.
       * Two-instance note: AppSidebar mounts its own SettingsPanel/Form for
       * desktop. Both can coexist because workspace data is read from context
       * (no duplicated fetches at the panel level), and only one is ever open.
       */}
      <MobileWorkspaceDrawer
        open={mobileWorkspaceDrawerOpen}
        onOpenChange={setMobileWorkspaceDrawerOpen}
        onOpenWorkspaceSettings={(section) => {
          setMobileWorkspaceSettingsSection(section ?? 'general')
          setMobileWorkspaceSettingsEverOpened(true)
          setMobileWorkspaceSettingsOpen(true)
        }}
        onOpenCreateWorkspace={() => {
          setMobileCreateWorkspaceEverOpened(true)
          setMobileCreateWorkspaceOpen(true)
        }}
      />
      {/*
       * Lazy-mount the mobile settings/create panels: they only enter the tree
       * after the user has triggered them at least once. This avoids paying
       * WorkspaceSettingsPanel's on-mount data fetch a second time on every
       * workspace change (AppSidebar already mounts its own copy for desktop).
       */}
      {mobileWorkspaceSettingsEverOpened ? (
        <SlideOutPanel
          open={mobileWorkspaceSettingsOpen}
          onOpenChange={setMobileWorkspaceSettingsOpen}
          title="Workspace Settings"
          direction="right"
          className="w-full lg:max-w-md z-[9999999]"
        >
          <WorkspaceSettingsPanel
            open={mobileWorkspaceSettingsOpen}
            onOpenChange={setMobileWorkspaceSettingsOpen}
            defaultOpenSection={mobileWorkspaceSettingsSection}
          />
        </SlideOutPanel>
      ) : null}
      {mobileCreateWorkspaceEverOpened ? (
        <SlideOutPanel
          open={mobileCreateWorkspaceOpen}
          onOpenChange={setMobileCreateWorkspaceOpen}
          title="Create workspace"
          direction="right"
          className="w-full lg:max-w-md z-[9999999]"
        >
          <WorkspaceForm
            onSuccess={() => setMobileCreateWorkspaceOpen(false)}
            onCancel={() => setMobileCreateWorkspaceOpen(false)}
          />
        </SlideOutPanel>
      ) : null}
      {completingOnboarding && (
        <div className="fixed inset-0 z-[130] flex flex-col items-center justify-center bg-atrium-background animate-in fade-in duration-700">
          <p className="text-2xl font-semibold text-atrium-black tracking-tight">Welcome to Atrium</p>
          <p className="mt-2 text-sm text-atrium-black/50">Taking you to your dashboard...</p>
        </div>
      )}
      {isLoggedIn ? <DataRefreshListener /> : null}
      <GlobalDropOverlay />
    </MobileHeaderProvider>
  )
}

function MobileAccountAvatar({ refreshKey }: { refreshKey: number }) {
  const { user, isLoading: userLoading } = useCachedUser()
  const supabase = useMemo(() => createClient(), [])
  const [publicProfile, setPublicProfile] = useState<Tables<'public_profiles'> | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    const fetchProfile = async () => {
      if (!user) {
        setPublicProfile(null)
        setAvatarUrl(null)
        setProfileLoading(false)
        return
      }

      setProfileLoading(true)
      try {
        const { data: profile, error } = await supabase
          .from('public_profiles')
          .select('*')
          .eq('auth_user_id', user.id)
          .single()

        if (cancelled) return

        if (error) {
          setPublicProfile(null)
          setAvatarUrl(null)
          return
        }

        const nextProfile = profile as Tables<'public_profiles'>
        setPublicProfile(nextProfile)

        if (nextProfile.avatar_path) {
          const { data } = supabase.storage.from('avatars').getPublicUrl(nextProfile.avatar_path)
          setAvatarUrl(data.publicUrl)
        } else {
          setAvatarUrl(null)
        }
      } catch {
        if (!cancelled) {
          setPublicProfile(null)
          setAvatarUrl(null)
        }
      } finally {
        if (!cancelled) setProfileLoading(false)
      }
    }

    fetchProfile()
    return () => {
      cancelled = true
    }
  }, [user, refreshKey, supabase])

  if (userLoading || profileLoading) {
    return <Skeleton className="size-8 rounded-full bg-atrium-border/60" />
  }

  const name = publicProfile?.display_name?.trim() ?? user?.email ?? ''
  const initial = name[0]?.toUpperCase() ?? ''

  return (
    <Avatar className="size-8 rounded-full">
      <AvatarImage src={avatarUrl || undefined} className="object-cover" />
      <AvatarFallback className="flex size-full items-center justify-center rounded-full bg-linear-to-br from-accent-400 via-accent-600 to-accent-800 text-xs font-medium text-white">
        {initial}
      </AvatarFallback>
    </Avatar>
  )
}

export default function LobbyLayoutClient({
  children,
  chatSlot,
  modalSlot,
  initialSidebarRailMode,
  initialChatWasOpen,
  initialShowLoggedOutBanner,
}: {
  children: React.ReactNode
  chatSlot: React.ReactNode
  modalSlot: React.ReactNode
  initialSidebarRailMode: SidebarRailMode | null
  initialChatWasOpen: boolean
  initialShowLoggedOutBanner: boolean
}) {
  const { isLoggedIn, isLoading } = useCachedUser()
  const showLoggedOutBanner = isLoading ? initialShowLoggedOutBanner : !isLoggedIn
  const content = (
    <LobbyInner
      isLoggedIn={isLoggedIn}
      showLoggedOutBanner={showLoggedOutBanner}
      initialSidebarRailMode={initialSidebarRailMode}
    >
      {children}
    </LobbyInner>
  )

  return (
    <SearchProvider>
      <ChatProvider initialChatWasOpen={initialChatWasOpen}>
        <PageStateProvider>
          <ShellPanelsProvider>
            <WorkspaceProvider>
              <div className="contents">
                <Fragment key="lobby-content">{content}</Fragment>
                <Fragment key="lobby-chat">{chatSlot}</Fragment>
                <Fragment key="lobby-modal">{modalSlot}</Fragment>
              </div>
            </WorkspaceProvider>
          </ShellPanelsProvider>
        </PageStateProvider>
      </ChatProvider>
    </SearchProvider>
  )
}
